import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import parentChildService, { TestResult, ChildProgress } from '@/shared/services/parentChildService';
import { getErrorMessage } from '../../shared/utils/errorUtils';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000/api/v1';

// Types
interface ChildInfo {
  id: string;
  full_name: string;
  phone_last_four: string;
  grade: string;
  school_name: string;
  birth_year: number;
}

interface Connection {
  connection_id: string;
  parent_id?: string;
  child_id?: string;
  connection_status: string;
  connected_at: string;
  notify_on_test_complete: boolean;
  notify_on_score_change: boolean;
  child?: {
    full_name: string;
    grade: string;
    school_name: string;
    birth_year: number;
    test_count: number;
    last_test_date: string | null;
  };
  parent?: {
    full_name: string;
    phone_last_four: string;
  };
}

interface PendingRequest {
  id: string;
  parent_id: string;
  parent_info: {
    full_name: string;
    phone_last_four: string;
    email: string;
  };
  requested_at: string;
}

interface ParentChildState {
  searchedChild: ChildInfo | null;
  myChildren: Connection[];
  myParents: Connection[];
  pendingRequests: PendingRequest[];
  childTestResults: TestResult[];
  childProgress: ChildProgress | null;
  loading: boolean;
  error: string | null;
}

const initialState: ParentChildState = {
  searchedChild: null,
  myChildren: [],
  myParents: [],
  pendingRequests: [],
  childTestResults: [],
  childProgress: null,
  loading: false,
  error: null,
};

// Async Thunks
export const searchChild = createAsyncThunk(
  'parentChild/searchChild',
  async (phoneNumber: string, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(
        `${API_URL}/parent-child/search-child`,
        { phone_number: phoneNumber },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data.data;
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, '검색에 실패했습니다'));
    }
  }
);

export const requestConnection = createAsyncThunk(
  'parentChild/requestConnection',
  async (childPhoneNumber: string, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(
        `${API_URL}/parent-child/request`,
        { child_phone_number: childPhoneNumber },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data.data;
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, '요청에 실패했습니다'));
    }
  }
);

export const fetchMyChildren = createAsyncThunk(
  'parentChild/fetchMyChildren',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API_URL}/parent-child/my-children`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data.data;
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, '목록 조회에 실패했습니다'));
    }
  }
);

export const fetchMyParents = createAsyncThunk(
  'parentChild/fetchMyParents',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API_URL}/parent-child/my-parents`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data.data;
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, '부모 목록 조회에 실패했습니다'));
    }
  }
);

export const fetchPendingRequests = createAsyncThunk(
  'parentChild/fetchPendingRequests',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API_URL}/parent-child/pending-requests`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data.data;
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, '요청 조회에 실패했습니다'));
    }
  }
);

export const approveConnection = createAsyncThunk(
  'parentChild/approveConnection',
  async (connectionId: string, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(
        `${API_URL}/parent-child/${connectionId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data.data;
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, '승인에 실패했습니다'));
    }
  }
);

export const rejectConnection = createAsyncThunk(
  'parentChild/rejectConnection',
  async (connectionId: string, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(
        `${API_URL}/parent-child/${connectionId}/reject`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data.data;
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, '거절에 실패했습니다'));
    }
  }
);

export const disconnectConnection = createAsyncThunk(
  'parentChild/disconnectConnection',
  async (connectionId: string, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(
        `${API_URL}/parent-child/${connectionId}/disconnect`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data.data;
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, '연결 해제에 실패했습니다'));
    }
  }
);

export const fetchChildTestResults = createAsyncThunk(
  'parentChild/fetchChildTestResults',
  async (childId: string, { rejectWithValue }) => {
    try {
      const results = await parentChildService.getChildTestResults(childId);
      return results;
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, '테스트 결과 조회에 실패했습니다'));
    }
  }
);

export const fetchChildProgress = createAsyncThunk(
  'parentChild/fetchChildProgress',
  async (childId: string, { rejectWithValue }) => {
    try {
      const progress = await parentChildService.getChildProgress(childId);
      return progress;
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, '진척도 조회에 실패했습니다'));
    }
  }
);

const parentChildSlice = createSlice({
  name: 'parentChild',
  initialState,
  reducers: {
    clearSearchedChild: (state) => {
      state.searchedChild = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearChildData: (state) => {
      state.childTestResults = [];
      state.childProgress = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Search Child
      .addCase(searchChild.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchChild.fulfilled, (state, action) => {
        state.loading = false;
        state.searchedChild = action.payload;
      })
      .addCase(searchChild.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Request Connection
      .addCase(requestConnection.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(requestConnection.fulfilled, (state) => {
        state.loading = false;
        state.searchedChild = null;
      })
      .addCase(requestConnection.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch My Children
      .addCase(fetchMyChildren.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMyChildren.fulfilled, (state, action) => {
        state.loading = false;
        state.myChildren = action.payload;
      })
      .addCase(fetchMyChildren.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch My Parents
      .addCase(fetchMyParents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMyParents.fulfilled, (state, action) => {
        state.loading = false;
        state.myParents = action.payload;
      })
      .addCase(fetchMyParents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch Pending Requests
      .addCase(fetchPendingRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPendingRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.pendingRequests = action.payload;
      })
      .addCase(fetchPendingRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Approve/Reject/Disconnect
      .addCase(approveConnection.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(rejectConnection.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(disconnectConnection.fulfilled, (state) => {
        state.loading = false;
      })
      // Fetch Child Test Results
      .addCase(fetchChildTestResults.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchChildTestResults.fulfilled, (state, action) => {
        state.loading = false;
        state.childTestResults = action.payload;
      })
      .addCase(fetchChildTestResults.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch Child Progress
      .addCase(fetchChildProgress.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchChildProgress.fulfilled, (state, action) => {
        state.loading = false;
        state.childProgress = action.payload;
      })
      .addCase(fetchChildProgress.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearSearchedChild, clearError, clearChildData } = parentChildSlice.actions;
export default parentChildSlice.reducer;
